import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import {
  getJobProfileDrafts,
  archiveJobProfile,
  unarchiveJobProfile,
  bulkPublishDraftProfiles,
  bulkArchiveDraftProfiles,
  bulkUnarchiveDraftProfiles,
} from '@/api/jobProfile';
import {
  isEmpty, isEqual, omit, pick,
} from 'lodash-es';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppPagination } from '@/stores/app/pagination';
import { useFilters } from '@/stores/filters/filters';
import { removeEmptyParams, previewQueryParams } from '@/stores/filters/helpers';
import { useAppRoute } from '@/stores/app/route';
import {
  BULK_ARCHITECTURE_ACTION_LIMIT,
  formatBulkArchiveResultMessage,
  formatBulkPublishResultMessage,
  getBulkActionCounts,
  formatBulkUnarchiveResultMessage,
} from '@/components/jobArchitecture/helpers';

export const useJobProfileDrafts = defineStore('jobProfileDrafts', () => {
  const { setLoading } = useAppLoading();
  const { updatePagination } = useAppPagination();
  const { updateFilters } = useFilters();
  const routeStore = useAppRoute();

  const { myJobsEnabled } = storeToRefs(useFilters());

  const jobProfileDrafts = ref([]);
  const totals = ref([]);
  const textFilter = ref('');
  const selectedProfiles = ref(new Set());
  const bulkActionLoading = ref(false);

  // On the first visit, show only active drafts.
  const showStatusDefault = ref(true);

  const setTextFilter = (value) => {
    textFilter.value = value;
  };

  const isArchivedDraft = (row) => Boolean(row?.is_archived);

  const hasSelectedArchived = computed(() => {
    const ids = selectedProfiles.value;
    const list = jobProfileDrafts.value;

    if (!ids?.size || !list?.length) return false;

    return list.some((row) => ids.has(row.ulid) && isArchivedDraft(row));
  });

  const hasSelectedActive = computed(() => {
    const ids = selectedProfiles.value;
    const list = jobProfileDrafts.value;

    if (!ids?.size || !list?.length) return false;

    return list.some((row) => ids.has(row.ulid) && !isArchivedDraft(row));
  });

  const resetSelectedProfiles = () => {
    selectedProfiles.value = new Set();
  };

  const resetStatusDefault = () => {
    showStatusDefault.value = true;
  };

  const loadJobProfileDrafts = async ({
    mode,
    routeQuery,
    router,
    selectedFilters,
    page,
    searchTerm: newSearchTerm,
    selectedMyJobs,
    selectedSubMenuOption: subMenuOption,
  }) => {
    setLoading({ pageLoading: true, listLoading: true });

    try {
      let resolvedRouteQuery = routeQuery ? { ...routeQuery } : {};

      if (showStatusDefault.value) {
        resolvedRouteQuery = isEmpty(resolvedRouteQuery)
          ? { status: 'active' }
          : { ...resolvedRouteQuery, status: resolvedRouteQuery.status || 'active' };

        showStatusDefault.value = false;
      }

      // Preview params only open the side panel. Keep them out of the library request.
      const previewParams = pick(resolvedRouteQuery, previewQueryParams);
      const params = omit(resolvedRouteQuery, previewQueryParams);

      if (!params.sort && !subMenuOption) {
        params.sort = '-created_at';
      } else if (subMenuOption) {
        params.sort = subMenuOption;
      }

      params.page = page || undefined;

      const parameters = {
        mode,
        params,
        selectedMyJobs,
        searchTerm: newSearchTerm,
        selectedFilters,
      };

      const newJobProfileDrafts = await getJobProfileDrafts(parameters);

      jobProfileDrafts.value = newJobProfileDrafts.documents;
      totals.value = newJobProfileDrafts.totals;

      updatePagination(newJobProfileDrafts.pagination);

      updateFilters({
        filters: newJobProfileDrafts.filters,
        subMenuOptions: newJobProfileDrafts.sorting,
        myJobsEnabled: newJobProfileDrafts.owned,
      });

      if (
        router
        && !isEqual(
          removeEmptyParams(omit(routeStore.route?.query, previewQueryParams)),
          removeEmptyParams(params),
        )
      ) {
        const newQuery = { ...selectedFilters, ...params, ...previewParams };

        if (myJobsEnabled.value) {
          newQuery.owned = myJobsEnabled.value;
        }

        if (textFilter.value) {
          newQuery.q = textFilter.value;
        }

        router.push({
          query: newQuery,
        }).catch(() => true);
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile drafts load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ pageLoading: false, listLoading: false });
    }
  };

  const reloadDraftLibrary = async (reloadParams = {}) => {
    await loadJobProfileDrafts({
      mode: 'draft',
      routeQuery: reloadParams.routeQuery,
      router: reloadParams.router,
      page: reloadParams.page,
      searchTerm: reloadParams.searchTerm ?? textFilter.value,
      selectedMyJobs: reloadParams.selectedMyJobs,
      selectedFilters: reloadParams.selectedFilters,
    });
  };

  const bulkPublishSelected = async (reloadParams = {}) => {
    let ulids = Array.from(selectedProfiles.value);
    if (!ulids.length) return;

    if (ulids.length > BULK_ARCHITECTURE_ACTION_LIMIT) {
      useAppSnackbar().addErrorSnackbar({
        text: `You can only publish ${BULK_ARCHITECTURE_ACTION_LIMIT} job profiles at once.`,
      });
      ulids = ulids.slice(0, BULK_ARCHITECTURE_ACTION_LIMIT);
    }

    const count = ulids.length;

    bulkActionLoading.value = true;

    try {
      const { results, count: successCount } = await bulkPublishDraftProfiles({ ulids });

      const text = formatBulkPublishResultMessage(results, successCount ?? count);

      useAppSnackbar().addSuccessSnackbar({
        text,
        link: { text: 'View them', to: { name: 'published-job-profiles' } },
      });

      resetSelectedProfiles();

      if (reloadParams.router) {
        await reloadDraftLibrary(reloadParams);
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Failed to publish job profiles — ${err.message || err}`,
      });

      throw err;
    } finally {
      bulkActionLoading.value = false;
    }
  };

  const bulkArchiveSelected = async (reloadParams = {}) => {
    let ulids = Array.from(selectedProfiles.value);

    if (!ulids.length) return { successCount: 0, failedCount: 0 };

    if (ulids.length > BULK_ARCHITECTURE_ACTION_LIMIT) {
      useAppSnackbar().addErrorSnackbar({
        text: `You can only archive ${BULK_ARCHITECTURE_ACTION_LIMIT} job profiles at once.`,
      });

      ulids = ulids.slice(0, BULK_ARCHITECTURE_ACTION_LIMIT);
    }

    const count = ulids.length;

    bulkActionLoading.value = true;

    try {
      const { results, count: successCount } = await bulkArchiveDraftProfiles({ ulids });

      const counts = getBulkActionCounts(results, successCount ?? count);

      // When some drafts were skipped, the bulk window explains it. No snackbar then.
      if (counts.failedCount === 0) {
        useAppSnackbar().addSuccessSnackbar({
          text: formatBulkArchiveResultMessage(results, successCount ?? count),
        });
      }

      resetSelectedProfiles();

      if (reloadParams.router) {
        await reloadDraftLibrary(reloadParams);
      }

      return counts;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Failed to archive job profiles — ${err.message || err}`,
      });

      throw err;
    } finally {
      bulkActionLoading.value = false;
    }
  };

  const bulkUnarchiveSelected = async (reloadParams = {}) => {
    let ulids = Array.from(selectedProfiles.value);

    if (!ulids.length) return { successCount: 0, failedCount: 0 };

    if (ulids.length > BULK_ARCHITECTURE_ACTION_LIMIT) {
      useAppSnackbar().addErrorSnackbar({
        text: `You can only unarchive ${BULK_ARCHITECTURE_ACTION_LIMIT} job profiles at once.`,
      });

      ulids = ulids.slice(0, BULK_ARCHITECTURE_ACTION_LIMIT);
    }

    const count = ulids.length;

    bulkActionLoading.value = true;

    try {
      const { results, count: successCount } = await bulkUnarchiveDraftProfiles({ ulids });

      const counts = getBulkActionCounts(results, successCount ?? count);

      // When some drafts were skipped, the bulk window explains it. No snackbar then.
      if (counts.failedCount === 0) {
        useAppSnackbar().addSuccessSnackbar({
          text: formatBulkUnarchiveResultMessage(results, successCount ?? count),
        });
      }

      resetSelectedProfiles();

      if (reloadParams.router) {
        await reloadDraftLibrary(reloadParams);
      }

      return counts;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Failed to unarchive job profiles — ${err.message || err}`,
      });

      throw err;
    } finally {
      bulkActionLoading.value = false;
    }
  };

  const archiveJobProfileByUlid = async ({ ulid }) => {
    if (!ulid) return false;

    try {
      await archiveJobProfile({ ulid });

      useAppSnackbar().addSuccessSnackbar({
        text: 'Job Profile successfully archived.',
      });

      return true;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile archive draft error: ${err.message || err}`,
      });

      return false;
    }
  };

  const unarchiveJobProfileByUlid = async ({ ulid }) => {
    if (!ulid) return false;

    try {
      await unarchiveJobProfile({ ulid });

      useAppSnackbar().addSuccessSnackbar({
        text: 'Job Profile successfully unarchived.',
      });

      return true;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile unarchive draft error: ${err.message || err}`,
      });

      return false;
    }
  };

  return {
    // State
    jobProfileDrafts,
    textFilter,
    selectedProfiles,
    bulkActionLoading,
    // Computed
    hasSelectedArchived,
    hasSelectedActive,
    // Mutations
    setTextFilter,
    resetSelectedProfiles,
    resetStatusDefault,
    // Actions
    loadJobProfileDrafts,
    bulkPublishSelected,
    bulkArchiveSelected,
    bulkUnarchiveSelected,
    archiveJobProfileByUlid,
    unarchiveJobProfileByUlid,
  };
});
